import { useEffect, useRef, useState } from "react";
import { io, type Socket } from "socket.io-client";
import { API_BASE } from "../api/client";
import type { Caption, PartialCaption, SessionStatus } from "../api/types";
import type { AudioChunk } from "./useAudioPlayer";

/** Hide a provisional line that hasn't been refreshed for this long. */
const PARTIAL_STALE_MS = 8000;

export type ConnState = "connecting" | "connected" | "reconnecting" | "disconnected";

const MAX_CAPTIONS = 200;

/**
 * Adds a caption in speaking order. The server already releases captions in order, so this normally
 * just appends; it only matters when something arrives late (a slow chunk given up on, or a burst
 * after a reconnect), which must not appear below captions spoken after it.
 */
export function insertInOrder(list: Caption[], caption: Caption): Caption[] {
  let at = list.length;
  while (at > 0 && list[at - 1].sequenceNumber > caption.sequenceNumber) at--;
  const next = at === list.length ? [...list, caption] : [...list.slice(0, at), caption, ...list.slice(at)];
  return next.length > MAX_CAPTIONS ? next.slice(-MAX_CAPTIONS) : next;
}

interface JoinAck {
  ok: boolean;
  sessionId?: string;
  status?: SessionStatus;
  sourceLanguageCode?: string;
  /** True when the server can speak captions with a neural voice for this listener's language. */
  serverAudio?: boolean;
  error?: string;
}

export interface ListenAudioOptions {
  /** The listener turned sound on. */
  enabled: boolean;
  onAudio: (chunk: AudioChunk) => void;
}

/**
 * Owns the /listen socket for one (sessionCode, languageCode) pair. Handles
 * reconnection (Socket.IO's own exponential backoff) and re-joins the room
 * automatically after a reconnect, since Socket.IO does not remember rooms
 * across a dropped connection.
 */
export function useListenSocket(sessionCode: string | null, languageCode: string | null, audio?: ListenAudioOptions) {
  const [serverAudio, setServerAudio] = useState(false);
  // Read through refs so toggling sound never tears down and reconnects the socket.
  const audioEnabledRef = useRef(false);
  audioEnabledRef.current = !!audio?.enabled;
  const onAudioRef = useRef(audio?.onAudio);
  onAudioRef.current = audio?.onAudio;
  const serverAudioRef = useRef(false);
  const [connState, setConnState] = useState<ConnState>("connecting");
  const [sessionStatus, setSessionStatus] = useState<SessionStatus | null>(null);
  const [captions, setCaptions] = useState<Caption[]>([]);
  const [joinError, setJoinError] = useState<string | null>(null);
  // The phrase being spoken right now (provisional, replaced in place until the
  // permanent caption for the same sequenceNumber arrives).
  const [partial, setPartial] = useState<PartialCaption | null>(null);
  const socketRef = useRef<Socket | null>(null);
  const lastFinalSeq = useRef(0);

  // A provisional line that stops being updated (speaker went silent, a network
  // blip swallowed the "clear") must not sit on screen forever.
  useEffect(() => {
    if (!partial) return;
    const timer = setTimeout(() => setPartial(null), PARTIAL_STALE_MS);
    return () => clearTimeout(timer);
  }, [partial]);

  useEffect(() => {
    if (!sessionCode || !languageCode) return;
    setCaptions([]);
    setPartial(null);
    lastFinalSeq.current = 0;
    setJoinError(null);
    setConnState("connecting");

    // API_BASE is "" (relative) unless the frontend is built for a separately-hosted
    // backend (see api/client.ts) — in which case this is an absolute cross-origin URL.
    const socket = io(`${API_BASE}/listen`, { transports: ["websocket", "polling"] });
    socketRef.current = socket;

    function doJoin() {
      socket.emit("join", { sessionCode, languageCode }, (ack: JoinAck) => {
        if (!ack.ok) {
          setJoinError(ack.error ?? "Unable to join session");
          return;
        }
        setSessionStatus(ack.status ?? null);
        serverAudioRef.current = !!ack.serverAudio;
        setServerAudio(!!ack.serverAudio);
        // Sound may already be on (e.g. after a reconnect): tell the server where to send it.
        if (ack.serverAudio && audioEnabledRef.current) socket.emit("audio:subscribe", { enabled: true });
      });
    }

    socket.on("connect", () => {
      setConnState("connected");
      doJoin();
    });
    socket.io.on("reconnect_attempt", () => setConnState("reconnecting"));
    socket.on("disconnect", () => setConnState("disconnected"));

    socket.on("caption:new", (payload: Omit<Caption, "receivedAt">) => {
      setCaptions((prev) => insertInOrder(prev, { ...payload, receivedAt: Date.now() }));
      // The permanent caption replaces the provisional line for the same (or an earlier) sequence.
      lastFinalSeq.current = Math.max(lastFinalSeq.current, payload.sequenceNumber);
      setPartial((current) => (current && current.sequenceNumber <= payload.sequenceNumber ? null : current));
    });

    socket.on("caption:partial", (payload: Omit<PartialCaption, "receivedAt">) => {
      // Empty text = the phrase ended: clear. A late provisional update for a
      // sequence that already has its permanent caption is stale: ignore it.
      if (!payload.text) return setPartial(null);
      if (payload.sequenceNumber <= lastFinalSeq.current) return;
      setPartial({ ...payload, receivedAt: Date.now() });
    });

    socket.on("session:state", (payload: { status: SessionStatus }) => {
      setSessionStatus(payload.status);
    });

    socket.on("caption:audio", (payload: AudioChunk) => onAudioRef.current?.(payload));

    return () => {
      socket.close();
      socketRef.current = null;
    };
  }, [sessionCode, languageCode]);

  // Turning sound on/off tells the server whether to send (and pay to synthesize) audio for this listener.
  const audioEnabled = !!audio?.enabled;
  useEffect(() => {
    const socket = socketRef.current;
    if (socket?.connected && serverAudioRef.current) socket.emit("audio:subscribe", { enabled: audioEnabled });
  }, [audioEnabled, serverAudio]);

  return { connState, sessionStatus, captions, partial, joinError, serverAudio };
}
